import React, { useEffect, useRef, useState } from 'react';
import { cityNear, fetchGeoConfig, GeoPlace, searchCities } from '../../../api/geo-client';
import { LocationAsk } from '../../location-ask/location-ask';
import {
  CitySuggestionCredit,
  CitySuggestionItem,
  CitySuggestionList,
  CityField,
  Input,
} from '../onboarding.styled';

export interface CityPoint {
  latitude: number;
  longitude: number;
}

interface Props {
  id: string;
  value: string;
  onChange: (city: string) => void;
  /**
   * The coordinates of a city PICKED from the list, or null as soon as the text
   * is typed over. The server derives the first building's time zone from them
   * — a city centre is not the building's pin, so they are used for nothing
   * else and are not stored.
   */
  onPick?: (point: CityPoint | null) => void;
  hasError?: boolean;
  placeholder?: string;
  /** Offer "use my location" for the city. Off on the signup wizard since its
   *  map arrived (2026-10-14): the map's own ask places the building's pin, and
   *  two location asks on one step is one too many. */
  askForLocation?: boolean;
}

const DEBOUNCE_MS = 250;
const MIN_CHARS = 3;

/**
 * The signup wizard's City field.
 *
 * Deliberately just an autocompleting TEXT INPUT — no map (owner ruling D-1:
 * the building is pinned later, on the buildings screen). It also stays a real
 * text box: with no address provider configured, or when the lookup fails, or
 * when the city simply is not in the list, whatever is typed is still accepted.
 *
 * IT ASKS FOR THE DEVICE'S LOCATION — OUR ask, under the field, not the
 * browser's (R-T10, WP-C11). The old automatic prompt appeared with no
 * explanation the moment the step opened and was removed for it. This one says
 * what the location is for and offers "Not now"; the browser's prompt follows a
 * tap. What comes back is the CITY the person is in, OFFERED as the first
 * suggestion — they pick it or type another, because where somebody is sitting
 * is often not where their building is. Their position is rounded to about a
 * kilometre before it leaves the browser and is not stored.
 *
 * The list is a keyboard listbox: arrows move, Enter picks, Escape closes.
 */
export const CityAutocomplete: React.FC<Props> = ({
  id,
  value,
  onChange,
  onPick,
  hasError,
  placeholder,
  askForLocation = true,
}) => {
  const [enabled, setEnabled] = useState(false);
  const [credit, setCredit] = useState<{ text: string; url: string } | null>(null);
  const [suggestions, setSuggestions] = useState<GeoPlace[]>([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);
  // The city the device is in, once the person has shared it. Offered first.
  const [near, setNear] = useState<GeoPlace | null>(null);
  // The option the arrow keys are on, or -1 for none (typing stays typing).
  const [active, setActive] = useState(-1);

  /* What the list shows: the nearby city first (when there is one), then what
     the search found, without the same city twice. */
  const options: { place: GeoPlace; nearYou: boolean }[] = [
    ...(near ? [{ place: near, nearYou: true }] : []),
    ...suggestions
      .filter(place => !near || (place.label || place.city) !== (near.label || near.city))
      .map(place => ({ place, nearYou: false })),
  ];

  const onLocated = async (position: { latitude: number; longitude: number }): Promise<void> => {
    const place = await cityNear(position.latitude, position.longitude);
    // No city there, or the lookup is off: offer nothing. The field still works.
    if (!place) return;
    setNear(place);
    setActive(-1);
    setOpen(true);
  };

  /* ── Is there an address provider at all? ── */
  useEffect(() => {
    let alive = true;

    fetchGeoConfig().then(config => {
      if (!alive || !config.geocoding) return;
      setEnabled(true);
      setCredit({
        text: config.geocoding.attribution,
        url: config.geocoding.attribution_url,
      });
    });

    return () => {
      alive = false;
    };
  }, []);

  /* ── Suggestions ── */
  useEffect(() => {
    if (!enabled || !open || value.trim().length < MIN_CHARS) {
      setSuggestions([]);

      return undefined;
    }

    let alive = true;
    const timer = setTimeout(async () => {
      const found = await searchCities(value.trim());
      // A slow answer to an old question must not replace a newer one.
      if (alive) setSuggestions(found);
    }, DEBOUNCE_MS);

    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [value, enabled, open]);

  const choose = (place: GeoPlace) => {
    // The city on its own ("Lisbon"); the label carries the country too
    // ("Lisbon, Portugal"), which is not what this column holds.
    onChange(place.city || place.label || '');
    onPick?.({ latitude: place.latitude, longitude: place.longitude });
    setOpen(false);
    setSuggestions([]);
    setActive(-1);
  };

  const optionId = (index: number) => `${id}-option-${index}`;
  const listId = `${id}-listbox`;
  const listOpen = open && options.length > 0;

  useEffect(() => {
    if (!open) return undefined;

    const onDown = (event: Event) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    };

    // The keyboard leaving counts too, or the list hangs open over the next field.
    document.addEventListener('mousedown', onDown);
    document.addEventListener('focusin', onDown);

    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('focusin', onDown);
    };
  }, [open]);

  return (
    <CityField ref={boxRef}>
      <Input
        id={id}
        value={value}
        autoComplete="off"
        hasError={hasError}
        placeholder={placeholder}
        onChange={e => {
          onChange(e.target.value);
          // Typed over: the coordinates of the city picked before no longer
          // describe what the box says.
          onPick?.(null);
          setOpen(true);
          setActive(-1);
        }}
        onFocus={() => setOpen(true)}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={listOpen}
        aria-controls={listOpen ? listId : undefined}
        aria-activedescendant={listOpen && active >= 0 ? optionId(active) : undefined}
        onKeyDown={e => {
          if (!listOpen) return;
          if (e.key === 'Escape') {
            setOpen(false);
            setActive(-1);
          } else if (e.key === 'ArrowDown') {
            // Arrows move through the list and wrap; the caret stays put.
            e.preventDefault();
            setActive(current => (current + 1) % options.length);
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive(current => (current <= 0 ? options.length - 1 : current - 1));
          } else if (e.key === 'Enter' && active >= 0 && options[active]) {
            // Only with an option highlighted: Enter otherwise still submits
            // the step, as it does in every other field.
            e.preventDefault();
            choose(options[active].place);
          }
        }}
      />
      {listOpen && (
        <CitySuggestionList role="listbox" id={listId}>
          {options.map(({ place, nearYou }, index) => (
            <CitySuggestionItem
              key={`${nearYou ? 'near' : 'found'}-${place.place_id || place.label}-${index}`}
              id={optionId(index)}
              type="button"
              role="option"
              aria-selected={index === active}
              data-active={index === active ? '' : undefined}
              data-near-you={nearYou ? '' : undefined}
              // Keep the keyboard in the field: a press must not move focus
              // into the list and close it before the click lands.
              tabIndex={-1}
              onMouseEnter={() => setActive(index)}
              onClick={() => choose(place)}
            >
              {nearYou ? 'Near you: ' : ''}
              {place.label || place.city || ''}
            </CitySuggestionItem>
          ))}
          {credit ? (
            <CitySuggestionCredit
              href={credit.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {credit.text}
            </CitySuggestionCredit>
          ) : null}
        </CitySuggestionList>
      )}
      {/* Only when there is a lookup to feed: with no address provider the
          position could not be turned into a city, and an ask with no purpose
          trains people to refuse. Not used silently either — on signup a
          location is only ever taken after a tap. */}
      {enabled && !near && askForLocation ? (
        <LocationAsk
          purpose="Share your location and we will suggest the city you are in. You can still type any city."
          onLocated={position => void onLocated(position)}
          useWhenGranted={false}
        />
      ) : null}
    </CityField>
  );
};
